import { Component } from '@theme/component';

/**
 * @typedef {object} CatalogueEntry
 * @property {number} id - Variant ID.
 * @property {boolean} available - Whether the variant can be added to the cart.
 * @property {number} pack - Pieces per pack (1 for single items).
 * @property {boolean} unit - True when the entry was reached through the unit SKU of a pack variant.
 * @property {string} sku - The variant SKU as stored in Shopify.
 *
 * @typedef {object} Problem
 * @property {number} line - 1-based line number in the textarea.
 * @property {string} text - The line as typed (trimmed).
 * @property {'notFound' | 'unavailable' | 'quantity'} reason
 *
 * @typedef {object} QuickOrderRefs
 * @property {HTMLFormElement} form
 * @property {HTMLTextAreaElement} input
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} status
 * @property {HTMLScriptElement} data
 */

/** Separators between SKU and quantity: comma, semicolon, tab or spaces. */
const SEPARATORS = /[,;\t]|\s+/;

/** Quantity token: digits, optionally followed by "pcs", "pc" or "x" (e.g. "4", "4pcs", "4x"). */
const QUANTITY = /^(\d{1,6})(?:pcs?|x)?$/i;

/** Standalone multiplier tokens that are ignored ("ZIOMB8V4 x 4"). */
const MULTIPLIERS = new Set(['x', 'X', '×']);

/**
 * Quick order by SKU for the home page.
 *
 * Reads "SKU, qty" lines, resolves each SKU against the catalogue JSON rendered by
 * `sections/knx-home-quick-order.liquid`, and adds every line to the cart in one `/cart/add.js`
 * request before opening the cart. While any line is unknown, unavailable or unreadable, nothing is
 * added and the lines are listed in the status live region.
 *
 * @extends Component<QuickOrderRefs>
 */
class KnxQuickOrderComponent extends Component {
  requiredRefs = ['form', 'input', 'submit', 'status', 'data'];

  /** @type {Map<string, CatalogueEntry> | null} */
  #catalogue = null;

  /** @type {AbortController | null} */
  #request = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.submit.disabled = false;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#catalogue = null;
    if (!this.#request) this.refs.submit.disabled = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
    this.#request = null;
  }

  /**
   * Handles the form submit routed by `on:submit="/handleSubmit"`.
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();
    if (this.#request) return;

    const { lines, problems } = this.#parse(this.refs.input.value);

    if (problems.length > 0) {
      this.#showProblems(problems);
      return;
    }

    if (lines.length === 0) {
      this.#showError([this.#message('msgEmpty')]);
      return;
    }

    this.#clearInvalid();
    this.#setStatus(this.#message('msgAdding'));
    this.refs.submit.disabled = true;
    this.setAttribute('aria-busy', 'true');

    const controller = new AbortController();
    this.#request = controller;

    try {
      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: lines }),
        signal: controller.signal,
      });

      /** @type {{ status?: number, message?: string, description?: string }} */
      const body = await response.json().catch(() => ({}));

      if (!response.ok || body.status) {
        const reason = body.description || body.message || String(response.status);
        this.#showError([`${this.#message('msgRejected')} ${reason}`, this.#message('msgCheck')], true);
        return;
      }

      this.#setStatus(this.#message('msgAdded'));
      window.location.assign(this.dataset.cartUrl || '/cart');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#showError([this.#message('msgNetwork')]);
    } finally {
      if (this.#request === controller) this.#request = null;
      this.removeAttribute('aria-busy');
      if (this.isConnected) this.refs.submit.disabled = false;
    }
  }

  /**
   * Splits the textarea into cart lines and problems. Lines for the same variant are merged.
   * A unit SKU of a pack variant is read as pieces and rounded up to whole packs.
   * @param {string} text
   * @returns {{ lines: Array<{ id: number, quantity: number }>, problems: Problem[] }}
   */
  #parse(text) {
    const catalogue = this.#getCatalogue();
    /** @type {Map<number, { packs: number, pieces: number, pack: number }>} */
    const totals = new Map();
    /** @type {Problem[]} */
    const problems = [];
    let firstContentLine = true;

    for (const [index, raw] of text.split(/\r?\n/).entries()) {
      const line = raw.trim();
      if (!line) continue;

      const tokens = line.split(SEPARATORS).filter((token) => token && !MULTIPLIERS.has(token));
      const [skuToken, quantityToken] = tokens;
      if (!skuToken) continue;

      const key = skuToken.toUpperCase();
      const isFirst = firstContentLine;
      firstContentLine = false;

      // A header row pasted from a spreadsheet ("SKU  Qty") is skipped.
      if (isFirst && key === 'SKU') continue;

      const lineNumber = index + 1;
      const match = quantityToken === undefined ? ['1', '1'] : QUANTITY.exec(quantityToken);
      const quantity = match ? Number(match[1]) : NaN;

      if (!Number.isInteger(quantity) || quantity < 1) {
        problems.push({ line: lineNumber, text: line, reason: 'quantity' });
        continue;
      }

      const entry = catalogue.get(key);
      if (!entry) {
        problems.push({ line: lineNumber, text: line, reason: 'notFound' });
        continue;
      }
      if (!entry.available) {
        problems.push({ line: lineNumber, text: line, reason: 'unavailable' });
        continue;
      }

      // Unit-SKU pieces are summed per variant first, then rounded up to whole packs once.
      const total = totals.get(entry.id) || { packs: 0, pieces: 0, pack: entry.pack };
      if (entry.unit && entry.pack > 1) {
        total.pieces += quantity;
      } else {
        total.packs += quantity;
      }
      totals.set(entry.id, total);
    }

    const lines = Array.from(totals, ([id, total]) => ({
      id,
      quantity: total.packs + Math.ceil(total.pieces / total.pack),
    }));
    return { lines, problems };
  }

  /**
   * Builds (once per render) the SKU → variant map from the JSON script.
   * Real SKUs win over unit-SKU aliases of pack variants.
   * @returns {Map<string, CatalogueEntry>}
   */
  #getCatalogue() {
    if (this.#catalogue) return this.#catalogue;

    /** @type {Map<string, CatalogueEntry>} */
    const catalogue = new Map();
    /** @type {Array<[string, CatalogueEntry]>} */
    const aliases = [];

    /** @type {unknown} */
    let rows = [];
    try {
      rows = JSON.parse(this.refs.data.textContent || '[]');
    } catch {
      rows = [];
    }

    if (Array.isArray(rows)) {
      for (const row of rows) {
        if (!Array.isArray(row)) continue;
        const [sku, id, available, pack, unitSku] = row;
        if (typeof sku !== 'string' || !sku || !id) continue;

        const packSize = Number(pack) > 1 ? Number(pack) : 1;
        const entry = { id: Number(id), available: available === true, pack: packSize, unit: false, sku };
        catalogue.set(sku.trim().toUpperCase(), entry);

        if (typeof unitSku === 'string' && unitSku.trim()) {
          aliases.push([unitSku.trim().toUpperCase(), { ...entry, unit: true }]);
        }
      }
    }

    for (const [key, entry] of aliases) {
      if (!catalogue.has(key)) catalogue.set(key, entry);
    }

    this.#catalogue = catalogue;
    return catalogue;
  }

  /**
   * Lists the problem lines in the live region and marks the field invalid.
   * @param {Problem[]} problems
   */
  #showProblems(problems) {
    const reasons = {
      notFound: this.#message('msgNotFound'),
      unavailable: this.#message('msgUnavailable'),
      quantity: this.#message('msgQuantity'),
    };

    const intro = document.createElement('p');
    intro.textContent = this.#message('msgIntro');

    const list = document.createElement('ul');
    for (const problem of problems) {
      const item = document.createElement('li');
      const code = document.createElement('code');
      code.textContent = problem.text;
      item.append(`${this.#message('msgLine')} ${problem.line}: `, code, ` — ${reasons[problem.reason]}`);
      list.append(item);
    }

    this.refs.input.setAttribute('aria-invalid', 'true');
    this.refs.status.dataset.state = 'error';
    this.refs.status.replaceChildren(intro, list);
  }

  /**
   * Shows error text in the live region.
   * @param {string[]} messages
   * @param {boolean} [linkToCart] - Append a link to the cart.
   */
  #showError(messages, linkToCart = false) {
    const nodes = messages.filter(Boolean).map((message) => {
      const paragraph = document.createElement('p');
      paragraph.textContent = message;
      return paragraph;
    });

    if (linkToCart && this.dataset.cartUrl) {
      const link = document.createElement('a');
      link.href = this.dataset.cartUrl;
      link.textContent = this.#message('msgCartLink') || this.dataset.cartUrl;
      nodes.at(-1)?.append(' ', link);
    }

    this.refs.input.setAttribute('aria-invalid', 'true');
    this.refs.status.dataset.state = 'error';
    this.refs.status.replaceChildren(...nodes);
  }

  /** @param {string} message */
  #setStatus(message) {
    delete this.refs.status.dataset.state;
    this.refs.status.textContent = message;
  }

  #clearInvalid() {
    this.refs.input.removeAttribute('aria-invalid');
  }

  /**
   * Reads a message from a `data-msg-*` attribute rendered by the section.
   * @param {string} key - camelCase dataset key, e.g. "msgNotFound"
   * @returns {string}
   */
  #message(key) {
    return this.dataset[key] || '';
  }
}

if (!customElements.get('knx-quick-order-component')) {
  customElements.define('knx-quick-order-component', KnxQuickOrderComponent);
}
